import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LiveSession } from '../services/liveSessionService';
import { getPublicProfile, ProfileRecord } from '../services/profileService';
import {
  listLiveAlgorithms, DEFAULT_ALGORITHM_ID, LiveFeedLocation,
} from '../services/liveFeedAlgorithms';
import { useLiveFeed } from '../hooks/useLiveFeed';
import AgentBadge from './AgentBadge';

/**
 * LiveDirectoryOverlay
 *
 * A "Live now" directory — the TikTok/Twitter-style feed of every
 * broadcast currently streaming on GeoTrust.  Solves the discovery
 * problem: without this overlay, a viewer had to know a
 * broadcaster's wallet or happen to find their marker on the map.
 *
 * Ordering is delegated to pluggable algorithms (see
 * `src/services/liveFeedAlgorithms.ts`) with a selector chip row so
 * users can switch between "Recent", "Nearby" and "Shuffle" — and
 * more algorithms can be added later without touching the UI.
 * Their preference is persisted in localStorage.
 */

const PREF_KEY = 'geotrust:liveFeedAlgo';

export interface LiveDirectoryOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  /** Current viewer's wallet — used to highlight "your own broadcast". */
  viewerWallet?: string | null;
  /** Viewer's GPS for the `proximity` algorithm. */
  viewerLocation?: LiveFeedLocation | null;
  /** Tap a broadcaster card → open their ProfileOverlay at Live tab. */
  onOpenProfile: (wallet: string) => void;
  /** Tap the "Go Live" shortcut → open owner's ProfileOverlay at Live tab. */
  onGoLive?: () => void;
}

function shortenAddress(addr: string | null | undefined): string {
  if (!addr) return '—';
  if (addr.length <= 10) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function formatRemaining(session: LiveSession): string {
  const end = session.scheduledEndAt ? new Date(session.scheduledEndAt).getTime() : 0;
  if (!end) return 'live';
  const remaining = Math.max(0, Math.floor((end - Date.now()) / 1000));
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  return `${m}:${s.toString().padStart(2, '0')} left`;
}

function formatLocation(s: LiveSession): string {
  if (s.approxLat == null || s.approxLng == null) return 'Location hidden';
  const latH = s.approxLat >= 0 ? 'N' : 'S';
  const lngH = s.approxLng >= 0 ? 'E' : 'W';
  return `${Math.abs(s.approxLat).toFixed(1)}° ${latH} · ${Math.abs(s.approxLng).toFixed(1)}° ${lngH}`;
}

function loadAlgoPref(): string {
  try {
    const v = window.localStorage.getItem(PREF_KEY);
    if (v) return v;
  } catch { /* ignore */ }
  return DEFAULT_ALGORITHM_ID;
}

export const LiveDirectoryOverlay: React.FC<LiveDirectoryOverlayProps> = ({
  isOpen, onClose, viewerWallet, viewerLocation, onOpenProfile, onGoLive,
}) => {
  const [algoId, setAlgoId] = useState<string>(() => loadAlgoPref());
  const algorithms = useMemo(() => listLiveAlgorithms(), []);

  // Tick every second so the "time left" label counts down smoothly
  // without us hitting the API more than once every poll interval.
  const [, setTick] = useState(0);

  // Polling is paused while the overlay is closed.
  const { sessions, loading, error, refresh } = useLiveFeed({
    algorithmId: algoId,
    viewerLocation,
    enabled: isOpen,
    viewerWallet,
  });

  useEffect(() => {
    try { window.localStorage.setItem(PREF_KEY, algoId); } catch { /* ignore */ }
  }, [algoId]);

  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  // Wallet → profile cache.
  const profileCacheRef = useRef<Map<string, ProfileRecord | null>>(new Map());
  const [profileRev, setProfileRev] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const unknown = sessions
        .map(s => s.wallet || s.broadcasterId)
        .filter((w): w is string => !!w && !profileCacheRef.current.has(w));
      for (const wallet of unknown) {
        try {
          const p = await getPublicProfile(wallet);
          if (cancelled) return;
          profileCacheRef.current.set(wallet, (p && p.version > 0) ? p : null);
        } catch {
          if (cancelled) return;
          profileCacheRef.current.set(wallet, null);
        }
        if (!cancelled) setProfileRev(r => r + 1);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, sessions]);

  const activeAlgo = algorithms.find(a => a.id === algoId) || algorithms[0];
  const proximityDisabled = !viewerLocation;

  const content = useMemo(() => {
    if (loading && sessions.length === 0) {
      return <div style={styles.empty}>Loading live broadcasts…</div>;
    }
    if (error && sessions.length === 0) {
      return (
        <div style={styles.empty}>
          <div style={{ color: '#fca5a5', marginBottom: 8 }}>⚠️ {error}</div>
          <button style={styles.linkBtn} onClick={refresh}>Try again</button>
        </div>
      );
    }
    if (sessions.length === 0) {
      return (
        <div style={styles.empty}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📡</div>
          <div style={{ fontWeight: 600, color: '#e5e7eb' }}>No one is live right now</div>
          <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>
            Be the first to go live — your broadcast will show up here for everyone to find.
          </div>
        </div>
      );
    }
    return (
      <div style={styles.list}>
        {sessions.map(session => {
          const wallet = session.wallet || session.broadcasterId;
          const profile = wallet ? profileCacheRef.current.get(wallet) : undefined;
          const isSelf  = !!viewerWallet && !!wallet && viewerWallet === wallet;
          const displayName =
            (profile && profile.displayName) || shortenAddress(wallet);
          const avatarUrl = profile?.avatarUrl;
          const bio = profile?.bio;
          return (
            <button
              key={session.id}
              type="button"
              style={{ ...styles.card, ...(isSelf ? styles.cardSelf : null) }}
              onClick={() => wallet && onOpenProfile(wallet)}
              title={wallet || undefined}
            >
              <div style={styles.avatarWrap}>
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    style={styles.avatar}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div style={styles.avatarFallback}>
                    {(displayName[0] || '?').toUpperCase()}
                  </div>
                )}
                <div style={styles.liveDot} />
              </div>
              <div style={styles.cardBody}>
                <div style={styles.cardTitle}>
                  <span style={{ fontWeight: 700 }}>{displayName}</span>
                  {isSelf && <span style={styles.selfBadge}>YOU</span>}
                  <AgentBadge wallet={wallet} compact />
                </div>
                {bio && <div style={styles.cardBio}>{bio}</div>}
                <div style={styles.cardMeta}>
                  <span>📍 {formatLocation(session)}</span>
                  <span>⏱ {formatRemaining(session)}</span>
                </div>
              </div>
              <div style={styles.cardCta}>Watch →</div>
            </button>
          );
        })}
      </div>
    );
    // profileRev intentional — forces re-render when cache updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, loading, error, refresh, viewerWallet, onOpenProfile, profileRev]);

  if (!isOpen) return null;

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div
        style={styles.sheet}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Live broadcasts"
      >
        <div style={styles.handle} />
        <div style={styles.header}>
          <div>
            <div style={styles.title}>
              <span style={styles.liveTitleDot} />
              Live now
            </div>
            <div style={styles.subtitle}>
              {sessions.length === 0
                ? 'No active broadcasts'
                : `${sessions.length} broadcast${sessions.length === 1 ? '' : 's'} · ${activeAlgo?.description ?? ''}`}
            </div>
          </div>
          <button type="button" style={styles.closeBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Algorithm chips — single source of truth for sort order,
            shared with the "next broadcast" swipe control. */}
        <div style={styles.chipRow}>
          {algorithms.map(a => {
            const active = a.id === algoId;
            const disabled = a.id === 'proximity' && proximityDisabled;
            return (
              <button
                key={a.id}
                type="button"
                disabled={disabled}
                onClick={() => setAlgoId(a.id)}
                title={disabled
                  ? 'Enable location to sort by proximity'
                  : a.description}
                style={{
                  ...styles.chip,
                  ...(active ? styles.chipActive : null),
                  ...(disabled ? styles.chipDisabled : null),
                }}
              >
                <span style={{ marginRight: 6 }}>{a.icon}</span>
                {a.label}
              </button>
            );
          })}
        </div>

        {onGoLive && viewerWallet && (
          <button
            type="button"
            style={styles.goLiveBtn}
            onClick={onGoLive}
          >
            <span style={{ fontSize: 18 }}>🎥</span>
            <span>Start your own broadcast</span>
          </button>
        )}

        <div style={styles.scrollArea}>
          {content}
        </div>
      </div>
    </div>
  );
};

// ── Inline styles ────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    zIndex: 10050,
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
  },
  sheet: {
    width: 'min(520px, 100vw)',
    maxHeight: 'min(85vh, 720px)',
    background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)',
    color: '#e5e7eb',
    borderTopLeftRadius: 24, borderTopRightRadius: 24,
    borderTop: '1px solid rgba(148,163,184,0.25)',
    borderLeft: '1px solid rgba(148,163,184,0.15)',
    borderRight: '1px solid rgba(148,163,184,0.15)',
    boxShadow: '0 -18px 48px rgba(0,0,0,0.6)',
    display: 'flex', flexDirection: 'column',
    paddingBottom: 'env(safe-area-inset-bottom, 12px)',
  },
  handle: {
    width: 44, height: 4,
    background: 'rgba(148,163,184,0.45)',
    borderRadius: 999,
    alignSelf: 'center',
    marginTop: 10, marginBottom: 4,
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 20px 6px',
  },
  title: {
    fontSize: 20, fontWeight: 700, color: '#f8fafc',
    display: 'inline-flex', alignItems: 'center', gap: 10,
  },
  liveTitleDot: {
    display: 'inline-block',
    width: 10, height: 10, borderRadius: '50%',
    background: '#ef4444',
    boxShadow: '0 0 0 4px rgba(239,68,68,0.25)',
    animation: 'pulse 1.5s ease-in-out infinite',
  } as React.CSSProperties,
  subtitle: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  closeBtn: {
    background: 'rgba(148,163,184,0.12)',
    color: '#e5e7eb', border: 'none',
    borderRadius: 12, width: 36, height: 36,
    fontSize: 16, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  chipRow: {
    display: 'flex', gap: 8,
    padding: '6px 20px 2px',
    flexWrap: 'wrap',
  },
  chip: {
    padding: '6px 12px', borderRadius: 999,
    border: '1px solid rgba(148,163,184,0.22)',
    background: 'rgba(30,41,59,0.75)',
    color: '#cbd5f5',
    fontSize: 12, fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center',
    transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
  },
  chipActive: {
    background: 'linear-gradient(135deg, rgba(239,68,68,0.25), rgba(185,28,28,0.18))',
    color: '#fff',
    borderColor: 'rgba(248,113,113,0.55)',
  },
  chipDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  goLiveBtn: {
    margin: '12px 20px 0',
    padding: '14px 18px',
    borderRadius: 16, border: 'none',
    background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
    color: '#fff', fontSize: 15, fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    boxShadow: '0 6px 18px rgba(239,68,68,0.35)',
  },
  scrollArea: {
    flex: 1, overflowY: 'auto',
    padding: '14px 16px 16px',
    WebkitOverflowScrolling: 'touch',
  } as React.CSSProperties,
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    display: 'flex', alignItems: 'center', gap: 12,
    width: '100%', padding: 12,
    borderRadius: 16,
    background: 'rgba(30,41,59,0.75)',
    border: '1px solid rgba(148,163,184,0.18)',
    color: '#e5e7eb', cursor: 'pointer',
    textAlign: 'left',
    transition: 'transform 120ms ease, background 120ms ease',
  },
  cardSelf: {
    background: 'linear-gradient(135deg, rgba(6,182,212,0.18), rgba(56,189,248,0.08))',
    borderColor: 'rgba(34,211,238,0.5)',
  },
  avatarWrap: { position: 'relative', width: 52, height: 52, flex: '0 0 auto' },
  avatar: {
    width: 52, height: 52, borderRadius: '50%', objectFit: 'cover',
    background: '#1e293b',
    border: '2px solid rgba(148,163,184,0.3)',
  },
  avatarFallback: {
    width: 52, height: 52, borderRadius: '50%',
    background: 'linear-gradient(135deg,#475569,#0f172a)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 20, fontWeight: 700, color: '#cbd5f5',
    border: '2px solid rgba(148,163,184,0.3)',
  },
  liveDot: {
    position: 'absolute', bottom: -2, right: -2,
    width: 16, height: 16, borderRadius: '50%',
    background: '#ef4444',
    border: '2px solid #0f172a',
    boxShadow: '0 0 0 3px rgba(239,68,68,0.35)',
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardTitle: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 15, color: '#f8fafc',
  },
  selfBadge: {
    fontSize: 10, fontWeight: 800,
    background: 'rgba(34,211,238,0.2)',
    color: '#67e8f9',
    border: '1px solid rgba(34,211,238,0.45)',
    padding: '2px 6px', borderRadius: 6,
    letterSpacing: 0.5,
  },
  cardBio: {
    fontSize: 12, color: '#94a3b8', marginTop: 2,
    display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  } as React.CSSProperties,
  cardMeta: {
    marginTop: 6, fontSize: 11, color: '#cbd5f5',
    display: 'flex', gap: 14, flexWrap: 'wrap',
  },
  cardCta: {
    fontSize: 13, fontWeight: 700, color: '#67e8f9',
    flex: '0 0 auto', paddingLeft: 4,
  },
  empty: { textAlign: 'center', padding: '36px 16px' },
  linkBtn: {
    background: 'transparent',
    border: '1px solid rgba(148,163,184,0.35)',
    color: '#67e8f9',
    padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
  },
};

export default LiveDirectoryOverlay;
